import { createLiveKitDiagnosticsProvider, type SupportDiagnosticsProvider } from '@web-front/support-chat';
import {
  getSupportLiveKitStats,
  getSupportRtcStats,
  getSupportRtcStatsHistory,
  isLiveKitStats,
  type SupportRtcStatsSample,
} from './supportRtcStatsStore';

type StatsMap = Record<string, unknown> | null | undefined;
type StatsRecord = Record<string, unknown>;

type RtcSummary = {
  rttMs: number | null;
  jitterMs: number | null;
  packetsLost: number;
  packetLossPercent: number | null;
  packetsReceived: number;
  packetsSent: number;
  bytesSent: number;
  bytesReceived: number;
  bitrateKbps: number | null;
  framesDropped: number;
  framesDecoded: number;
  freezeCount: number;
};

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function round(value: number | null, decimals = 2): number | null {
  return value == null ? null : Number(value.toFixed(decimals));
}

function summarizeStatsMap(stats: StatsMap): RtcSummary | null {
  if (!stats) return null;
  let rttMs: number | null = null;
  let jitterMs: number | null = null;
  let packetsLost = 0;
  let packetsReceived = 0;
  let packetsSent = 0;
  let bytesSent = 0;
  let bytesReceived = 0;
  let framesDropped = 0;
  let framesDecoded = 0;
  let freezeCount = 0;

  Object.values(stats).forEach((value) => {
    if (!value || typeof value !== 'object') return;
    const record = value as StatsRecord;
    if (record.type === 'candidate-pair' && record.currentRoundTripTime != null) {
      rttMs = Math.round(asNumber(record.currentRoundTripTime) * 1000);
    }
    if (record.type === 'inbound-rtp' || record.type === 'remote-inbound-rtp') {
      packetsLost += asNumber(record.packetsLost);
      packetsReceived += asNumber(record.packetsReceived);
      bytesReceived += asNumber(record.bytesReceived);
      framesDropped += asNumber(record.framesDropped);
      framesDecoded += asNumber(record.framesDecoded);
      freezeCount += asNumber(record.freezeCount);
      if (record.jitter != null) jitterMs = Math.round(asNumber(record.jitter) * 1000);
      if (record.roundTripTime != null) rttMs = Math.round(asNumber(record.roundTripTime) * 1000);
    }
    if (record.type === 'outbound-rtp' || record.type === 'remote-outbound-rtp') {
      packetsSent += asNumber(record.packetsSent);
      bytesSent += asNumber(record.bytesSent);
    }
  });

  const totalPackets = packetsReceived + packetsLost;
  return {
    rttMs,
    jitterMs,
    packetsLost,
    packetLossPercent: totalPackets > 0 ? round((packetsLost / totalPackets) * 100) : null,
    packetsReceived,
    packetsSent,
    bytesSent,
    bytesReceived,
    bitrateKbps: null,
    framesDropped,
    framesDecoded,
    freezeCount,
  };
}

function withBitrate(summary: RtcSummary | null, previous: RtcSummary | null, elapsedMs: number): RtcSummary | null {
  if (!summary || !previous || elapsedMs <= 0) return summary;
  const bytesDelta = Math.max(
    0,
    summary.bytesSent + summary.bytesReceived - previous.bytesSent - previous.bytesReceived,
  );
  return { ...summary, bitrateKbps: round((bytesDelta * 8) / elapsedMs) };
}

function summarizeHistory(samples: SupportRtcStatsSample[]) {
  let previousPublisher: RtcSummary | null = null;
  let previousSubscriber: RtcSummary | null = null;
  let previousTimestamp: number | null = null;
  const timeline = samples.map((sample) => {
    const livekit = isLiveKitStats(sample.report) ? sample.report : null;
    const elapsedMs = previousTimestamp == null ? 0 : sample.collectedAt - previousTimestamp;
    const publisher = withBitrate(summarizeStatsMap(livekit?.publisherStats), previousPublisher, elapsedMs);
    const subscriber = withBitrate(summarizeStatsMap(livekit?.subscriberStats), previousSubscriber, elapsedMs);
    previousPublisher = publisher;
    previousSubscriber = subscriber;
    previousTimestamp = sample.collectedAt;
    return { collectedAt: new Date(sample.collectedAt).toISOString(), publisher, subscriber };
  });

  const numeric = (values: Array<number | null>) => values.filter((value): value is number => value != null);
  const subscriber = timeline.map((sample) => sample.subscriber);
  const rtts = numeric(subscriber.map((sample) => sample?.rttMs ?? null));
  const jitters = numeric(subscriber.map((sample) => sample?.jitterMs ?? null));
  const losses = numeric(subscriber.map((sample) => sample?.packetLossPercent ?? null));
  const bitrates = numeric(subscriber.map((sample) => sample?.bitrateKbps ?? null));
  const average = (values: number[]) =>
    values.length ? round(values.reduce((sum, value) => sum + value, 0) / values.length) : null;

  return {
    timeline,
    trend: {
      sampleCount: timeline.length,
      rttMs: { average: average(rtts), max: rtts.length ? Math.max(...rtts) : null },
      jitterMs: { average: average(jitters), max: jitters.length ? Math.max(...jitters) : null },
      packetLossPercent: { average: average(losses), max: losses.length ? Math.max(...losses) : null },
      bitrateKbps: {
        latest: bitrates.at(-1) ?? null,
        min: bitrates.length ? Math.min(...bitrates) : null,
        max: bitrates.length ? Math.max(...bitrates) : null,
      },
      frameEvents: {
        dropped: subscriber.at(-1)?.framesDropped ?? 0,
        decoded: subscriber.at(-1)?.framesDecoded ?? 0,
        freezes: subscriber.at(-1)?.freezeCount ?? 0,
      },
    },
  };
}

function pick(record: StatsRecord, keys: string[]) {
  return Object.fromEntries(
    keys.flatMap((key) => {
      const value = record[key];
      return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? [[key, value]] : [];
    }),
  );
}

const RTP_FIELDS = [
  'packetsSent',
  'packetsReceived',
  'packetsLost',
  'bytesSent',
  'bytesReceived',
  'retransmittedPacketsSent',
  'retransmittedBytesSent',
  'jitter',
  'roundTripTime',
  'framesPerSecond',
  'frameWidth',
  'frameHeight',
  'framesEncoded',
  'framesDecoded',
  'framesDropped',
  'freezeCount',
  'pauseCount',
  'totalFreezeDuration',
  'totalPauseDuration',
  'nackCount',
  'pliCount',
  'firCount',
  'qualityLimitationReason',
  'jitterBufferDelay',
  'jitterBufferEmittedCount',
  'audioLevel',
  'totalAudioEnergy',
  'totalSamplesDuration',
];

function normalizeDetailedReport(publisherStats: StatsMap, subscriberStats: StatsMap) {
  const all = [...Object.values(publisherStats ?? {}), ...Object.values(subscriberStats ?? {})].filter(
    (value): value is StatsRecord => Boolean(value) && typeof value === 'object',
  );
  const codecs = new Map<string, StatsRecord>();
  all.filter((record) => record.type === 'codec').forEach((codec) => codecs.set(String(codec.id), codec));
  const media = { inbound: [] as StatsRecord[], outbound: [] as StatsRecord[], remoteInbound: [] as StatsRecord[] };

  for (const record of all) {
    if (record.type === 'inbound-rtp' || record.type === 'outbound-rtp' || record.type === 'remote-inbound-rtp') {
      const codec = codecs.get(String(record.codecId));
      const item = {
        direction: record.type,
        kind: record.kind ?? record.mediaType ?? 'unknown',
        codec: codec ? pick(codec, ['mimeType', 'clockRate', 'channels', 'sdpFmtpLine']) : null,
        ...pick(record, RTP_FIELDS),
        qualityLimitationDurations: normalizeNumericRecord(record.qualityLimitationDurations),
      };
      if (record.type === 'inbound-rtp') media.inbound.push(item);
      else if (record.type === 'outbound-rtp') media.outbound.push(item);
      else media.remoteInbound.push(item);
    }
  }

  const candidatePairs = all.filter((record) => record.type === 'candidate-pair');
  const selectedPair = candidatePairs.find(
    (record) => record.nominated === true || record.selected === true || record.state === 'succeeded',
  );
  const candidates = new Map(
    all
      .filter((record) => record.type === 'local-candidate' || record.type === 'remote-candidate')
      .map((record) => [String(record.id), record]),
  );
  const localCandidate = selectedPair ? candidates.get(String(selectedPair.localCandidateId)) : undefined;
  const remoteCandidate = selectedPair ? candidates.get(String(selectedPair.remoteCandidateId)) : undefined;
  const transport = all.find((record) => record.type === 'transport');

  return {
    collectedAt: new Date().toISOString(),
    connection: {
      transport: transport ? pick(transport, ['dtlsState', 'iceState', 'selectedCandidatePairChanges']) : null,
      selectedPair: selectedPair
        ? {
            pair: pick(selectedPair, [
              'state',
              'nominated',
              'currentRoundTripTime',
              'availableOutgoingBitrate',
              'availableIncomingBitrate',
              'bytesSent',
              'bytesReceived',
              'packetsSent',
              'packetsReceived',
              'requestsSent',
              'responsesReceived',
              'consentRequestsSent',
            ]),
            local: localCandidate
              ? pick(localCandidate, ['candidateType', 'protocol', 'relayProtocol', 'networkType'])
              : null,
            remote: remoteCandidate ? pick(remoteCandidate, ['candidateType', 'protocol']) : null,
          }
        : null,
    },
    media,
  };
}

function normalizeNumericRecord(value: unknown): Record<string, number> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.fromEntries(
    Object.entries(value as StatsRecord).flatMap(([key, nested]) => {
      const parsed = Number(nested);
      return Number.isFinite(parsed) ? [[key, parsed]] : [];
    }),
  );
}

export function createMeetDiagnosticsProvider(options: {
  connected?: boolean;
  participantIdentity?: string;
  microphoneEnabled?: boolean;
  cameraEnabled?: boolean;
}): SupportDiagnosticsProvider {
  return createLiveKitDiagnosticsProvider(async () => {
    const base = getSupportRtcStats();
    const livekit = options.connected ? getSupportLiveKitStats() : null;
    const history = livekit ? summarizeHistory(getSupportRtcStatsHistory()) : summarizeHistory([]);
    const publisher = history.timeline.at(-1)?.publisher ?? summarizeStatsMap(livekit?.publisherStats);
    const subscriber = history.timeline.at(-1)?.subscriber ?? summarizeStatsMap(livekit?.subscriberStats);
    const connectionState = options.connected ? 'connected' : 'disconnected';

    return {
      connectionState,
      localParticipant: { identity: options.participantIdentity || base?.localUserIdentity },
      // The snapshot leaves a missing side out; `aiSummary` below still says it is null.
      publisher: publisher ?? undefined,
      subscriber: subscriber ?? undefined,
      media: {
        microphoneEnabled: options.microphoneEnabled ?? (base ? base.isMicrophoneMuted === false : undefined),
        cameraEnabled: options.cameraEnabled ?? (base ? base.isCameraMuted === false : undefined),
      },
      aiSummary: {
        status: livekit ? 'rtc_available' : 'rtc_unavailable',
        connectionState,
        publisher,
        subscriber,
        trend: history.trend,
      },
      detailed: livekit
        ? {
            history: history.timeline,
            current: normalizeDetailedReport(livekit.publisherStats, livekit.subscriberStats),
          }
        : { reason: 'no_active_rtc_session' },
    };
  });
}
